import {CUSTOM_ELEMENTS_SCHEMA, inject, NgModule, provideAppInitializer} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {RouteReuseStrategy} from '@angular/router';
import {IonicModule, IonicRouteStrategy} from '@ionic/angular';
import {AppComponent} from './app.component';
import {AppRoutingModule} from './app-routing.module';
import {SharedModule} from './components/shared.module';
import {Menu} from './menu.component';
import {CommonModule} from '@angular/common';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {provideHttpClient} from '@angular/common/http';
import {environment} from '../environments/environment';
import {StoryApiModule, StoryConfiguration} from 'gamesis-story-client';
import {AuthLibModule, AUTH_CONFIG} from 'gamesis-auth';
import {PlayersModule} from './players/players.module';
import {LoginPage} from './public/login';
import {NotificationApiModule, NotificationConfiguration} from 'gamesis-notification-client';
import {StoreModule} from '@ngrx/store';
import {EffectsModule} from '@ngrx/effects';
import {StoreDevtoolsModule} from '@ngrx/store-devtools';
import {
    appReducer,
    CampaignEffects,
    campaignReducer,
    CharacterEffects,
    characterReducer,
    PlayerEffects,
    playerReducer,
    settingsReducer,
    StoryEffects,
    storyReducer
} from 'gamesis-state';
import {WebsocketEffects} from './effects/websocket.effects';
import {LoadingEffect} from './effects/loading.effect';
import {NotificationEffect} from './effects/notification.effect';
import {AuthEffects} from './effects/auth.effects';
import {AppEffects} from './effects/app.effects';
import {Capacitor} from '@capacitor/core';
import {StatusBar} from '@capacitor/status-bar';
import {EdgeToEdgeService} from './services/edgeToEdge';

export const initialize = (): (() => Promise<void>) => {
    return async () => {
        if (Capacitor.isPluginAvailable('StatusBar')) {
            await StatusBar.hide();
        }
    };
};

@NgModule({
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA
    ],
    declarations: [
        AppComponent,
        Menu,
        LoginPage
    ],
    imports: [
        BrowserModule,
        IonicModule.forRoot({
            mode: 'ios',
            backButtonText: '',
            swipeBackEnabled: false
        }),
        SharedModule,
        AppRoutingModule,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        AuthLibModule.forRoot(),
        PlayersModule,
        StoryApiModule.forRoot(() => {
            return new StoryConfiguration({
                basePath: environment.story
            });
        }),
        NotificationApiModule.forRoot(() => {
            return new NotificationConfiguration({
                basePath: environment.notification
            });
        }),
        StoreModule.forRoot({
            settings: settingsReducer,
            campaigns: campaignReducer,
            characters: characterReducer,
            story: storyReducer,
            players: playerReducer,
            app: appReducer
        }, {}),
        EffectsModule.forRoot([
            CampaignEffects,
            CharacterEffects,
            StoryEffects,
            PlayerEffects,
            AppEffects,
            AuthEffects,
            WebsocketEffects,
            LoadingEffect,
            NotificationEffect
        ]),
        StoreDevtoolsModule.instrument({maxAge: 25, trace: true})
    ],
    providers: [
        provideAppInitializer(initialize()),
        provideAppInitializer((): Promise<void> => {
            const edgeToEdgeService: EdgeToEdgeService = inject(EdgeToEdgeService);
            return edgeToEdgeService.init();
        }),
        provideHttpClient(),
        {provide: RouteReuseStrategy, useClass: IonicRouteStrategy},
        {provide: AUTH_CONFIG, useValue: {...environment.firebase}}
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
}
